import React, { useState, useContext, useRef } from 'react'
import {
    Grid,
    FormHelperText,
    TextField,
    InputAdornment,
    Divider,
    Select,
    MenuItem,
    Paper,
    FormControlLabel,
    Checkbox,
} from '@material-ui/core'
import { BrowserView, MobileView } from 'react-device-detect'
import FlexView from 'react-flexview/lib'
import { Controller, useForm } from 'react-hook-form'
import { ReactComponent as CompanyLogoSvg } from '../../../assets/company-logo.svg'
import { ReactComponent as CameraIconSvg } from '../../../assets/camera_add.svg'
import { ReactComponent as CameraChangeSvg } from '../../../assets/change-photo.svg'
import { DatePicker } from '@material-ui/pickers'
import { isOver18, awaitWithLoading } from '../../../services/utils'
import DateRangeIcon from '@material-ui/icons/DateRange'
import { SessionContextStore } from '../../../context/SessionContext'
import { OptionalTag } from '../../Core/OptionalTag'
import {
    FirebaseService,
    FirebaseConfigKey,
} from '../../../services/FirebaseService'
import {
    EventEmitterService,
    EventKey,
} from '../../../services/EventEmitterService'
import '../UserProfile.css'
import { STATE_EXPIRY_TTL, COUNTRIES } from '../../../Constants'
import {
    ACTIONS,
    UserProfileContextStore,
} from '../../../context/UserProfileContext'
import { Action } from '../../../models/Action'
import UserProfileModel from '../../../models/user-models/UserProfileModel'
import { SessionService } from '../../../services/SessionService'
import { UserApi } from '../../../api/user.api'
import { SkyElementHoverPopover } from '../../Core/Popover/SkyPopover'
import { ProfilePicturePopover } from '../../Core/Popover/ProfilePicturePopover'
import { useLocation } from 'react-router-dom'
import { ProposalSessionContextStore } from '../../../context/ProposalSessionContext'
import StorageService from '../../../services/StorageService'
import LocalizationService from '../../../services/LocalizationService'
const dayjs = require('dayjs')
const generalPhoneNumberRegex =
    /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/

export const OwnerSectionForm = ({
    defaultValues = {},
    SAVE_EVENT_KEY,
    onSave,
}) => {
    const location = useLocation()
    const { register, watch, trigger, setValue, errors, control, getValues } =
        useForm({ defaultValues: defaultValues, mode: 'onChange' })
    const profilePicture = useRef(null)
    const lnameWatch = watch('lname', defaultValues.lastName)
    const fnameWatch = watch('fname', defaultValues.firstName)
    const companyNameWatch = watch('company_name', defaultValues.company)
    const phoneNumberWatch = watch('phone_number', defaultValues.phoneNumber)
    const address1Watch = watch('address_line_1', '')
    const cityWatch = watch('city', '')
    const zipcodeWatch = watch('zipcode', '')
    const stateWatch = watch('state')
    const [states, setStates] = useState([])

    const validatePhoneNumber = (phoneNumber) =>
        !phoneNumber || generalPhoneNumberRegex.test(phoneNumber)

    const zipcodeRegex = new RegExp(
        FirebaseService.getValue(FirebaseConfigKey.ZIPCODE_REGEX)
    )
    const restrictedCountries: any[] = FirebaseService.getArray(
        FirebaseConfigKey.RESTRICTED_COUNTRIES
    )

    const setStateFromAutocomplete = (autoState) => {
        let selected = states.filter(
            (i) => i.name.toLowerCase() == autoState.toLowerCase()
        )
        if (selected.length > 0) setValue('state', selected[0].code)
    }

    const fetchStates = async () => {
        let res = await UserApi.getStates()

        if (res.ok) {
            setStates(res.parsedData)
            StorageService.setWithExpiry(
                'STATE_LIST',
                res.parsedData,
                STATE_EXPIRY_TTL
            )
        } else {
            EventEmitterService.dispatch(EventKey.ShowError, res)
        }
    }

    React.useEffect(() => {
        let id = EventEmitterService.subscribe(SAVE_EVENT_KEY, async () => {
            let valid = await trigger()
            if (!valid) return
            onSave(getValues())
        })

        return function cleanup() {
            EventEmitterService.unsubscribe(SAVE_EVENT_KEY, id)
        }
    }, [])

    React.useEffect(() => {
        if (StorageService.getWithExpiry('STATE_LIST')) {
            setStates(StorageService.getWithExpiry('STATE_LIST'))
        } else {
            fetchStates()
        }
    }, [])

    return (
        <div>
            <div className="text-body blue bold margin-bottom-basic">
                Details
            </div>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        InputLabelProps={{ shrink: !!fnameWatch }}
                        fullWidth
                        variant="outlined"
                        label="First name"
                        name="fname"
                        autoComplete="given-name"
                        autoFocus
                        inputRef={register({ required: true })}
                        error={!!errors.fname}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        InputLabelProps={{ shrink: !!lnameWatch }}
                        fullWidth
                        variant="outlined"
                        label="Last name"
                        name="lname"
                        autoComplete="family-name"
                        inputRef={register({ required: true })}
                        error={!!errors.lname}
                    />
                </Grid>
                <Grid item sm={12} md={6}>
                    <TextField
                        InputLabelProps={{ shrink: !!phoneNumberWatch }}
                        fullWidth
                        variant="outlined"
                        label="Phone number"
                        name="phone_number"
                        inputRef={register({ validate: validatePhoneNumber })}
                        autoComplete="tel"
                        error={!!errors.phone_number}
                        helperText={
                            !!errors.phone_number ? 'Invalid phone number' : ''
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="start">
                                    {' '}
                                    <OptionalTag />{' '}
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item sm={12} md={6}>
                    <TextField
                        InputLabelProps={{ shrink: !!companyNameWatch }}
                        fullWidth
                        variant="outlined"
                        type="number"
                        label="Social Security Number"
                        helperText={
                            'The Social Security Number allows us to give you a more accurate quote. if you prefer not to provide this information, leave this field blank'
                        }
                        name="social_security_number"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="start">
                                    {' '}
                                    <OptionalTag />{' '}
                                </InputAdornment>
                            ),
                        }}
                        inputRef={register}
                        error={!!errors.social_security_number}
                    />
                </Grid>
            </Grid>
            <div className="text-body blue bold margin-top-basic margin-bottom-basic">
                Address
            </div>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        InputLabelProps={{ shrink: !!address1Watch }}
                        fullWidth
                        variant="outlined"
                        label="Address line"
                        name="address_line_1"
                        inputRef={register({ required: true })}
                        error={!!errors.address_line_1}
                        helperText={!!errors.address_line_1 && 'Required'}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        InputLabelProps={{ shrink: !!cityWatch }}
                        fullWidth
                        variant="outlined"
                        label="City"
                        name="city"
                        helperText={!!errors.city && 'Required'}
                        inputRef={register({ required: true })}
                        error={!!errors.city}
                    />
                </Grid>

                <Grid item xs={6}>
                    <div className="full-width margin-right-basic">
                        <Controller
                            rules={{
                                required: true,
                                validate: (x) => x && x != 'statedefault',
                            }}
                            name="state"
                            as={
                                <TextField
                                    error={!!errors.state}
                                    fullWidth
                                    label={LocalizationService.STRINGS.state}
                                    select
                                    variant="outlined"
                                >
                                    <MenuItem disabled value={'statedefault'}>
                                        <div className="placeholder-text">
                                            {LocalizationService.STRINGS.state}
                                        </div>
                                    </MenuItem>
                                    {[...states, ...restrictedCountries]
                                        .sort((a, b) =>
                                            a.name.localeCompare(b.name)
                                        )
                                        .map((item) => (
                                            <MenuItem
                                                key={item.code}
                                                value={item.code}
                                            >
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                </TextField>
                            }
                            control={control}
                        />
                        {!!errors.state && (
                            <FormHelperText error={!!errors.state}>
                                {errors.state.type == 'validate'
                                    ? 'Not available'
                                    : 'Required'}
                            </FormHelperText>
                        )}
                    </div>
                    <TextField
                        name="state"
                        onChange={(e) => {
                            setStateFromAutocomplete(e.target.value)
                        }}
                        style={{ height: 0, width: 0 }}
                    />
                </Grid>

                <Grid item xs={6}>
                    <TextField
                        InputLabelProps={{ shrink: !!zipcodeWatch }}
                        fullWidth
                        variant="outlined"
                        label={LocalizationService.STRINGS.zipcode}
                        name="zipcode"
                        inputRef={register({
                            required: true,
                            validate: (x) => zipcodeRegex.test(x),
                        })}
                        helperText={
                            !!errors.zipcode &&
                            `Invalid ${LocalizationService.STRINGS.zipcode}`
                        }
                        error={!!errors.zipcode}
                    />
                </Grid>
            </Grid>
        </div>
    )
}
